<template>
    <DCard padding>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol
                class="pt-1"
            >
                <DSelect
                    id="type"
                    v-model="filterData.job"
                    :label="__('administrations.cron_jobs.executions.job')"
                    :options="allJobs"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="type"
                    v-model="filterData.status"
                    :label="__('administrations.cron_jobs.executions.status')"
                    :options="statusOptions"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DDateFormatInput
                    id="date_from"
                    v-model="filterData.dateFrom"
                    format="YYYY-MM-DD"
                    :label="__('administrations.cron_jobs.executions.started_at')"
                    class="mb-0"
                    type="date"
                    @keydown.enter.exact.prevent="changePage(executions.current_page)"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DDateFormatInput
                    id="date_to"
                    v-model="filterData.dateTo"
                    format="YYYY-MM-DD"
                    :label="__('administrations.cron_jobs.executions.completed_at')"
                    class="mb-0"
                    type="date"
                    @keydown.enter.exact.prevent="changePage(executions.current_page)"
                />
            </DCol>
            <DCol
                sm="3"
                class="d-flex gap-2"
            >
                <DButton
                    :label="__('messages.actions.filter')"
                    @click="changePage(executions.current_page)"
                />
                <DButton
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DAlert
            v-if="showNewDataHint"
            color="success"
            class="d-flex align-items-center new-data-hint"
            @click="refreshPage"
        >
            <DIcon
                name="alert"
                :size="24"
            />
            <div class="pr-2">
                {{ __('administrations.cron_jobs.executions.new_data_hint') }}
            </div>
        </DAlert>
        <DTable
            :items="data"
            :fields="fields"
            class="table-responsive"
        >
            <template #status="{item}">
                <td>
                    <DTag
                        :label="__('administrations.cron_jobs.executions.statuses.' + item.status)"
                        :color="getStatusTagColor(item.status)"
                    />
                </td>
            </template>
            <template #force="{item}">
                <td>
                    <DTag
                        :label="__('administrations.cron_jobs.executions.statuses.' + item.force)"
                        :color="getStatusTagColor(item.force)"
                    />
                </td>
            </template>
            <template #actions="{item}">
                <td>
                    <div style="display: flex; grid-gap: 10px">
                        <DButton
                            v-if="item.has_output"
                            icon="download"
                            mini
                            @click="downloadFile(item.id)"
                        />
                    </div>
                </td>
            </template>
        </DTable>
        <DPagination
            v-if="executions.last_page >= 1"
            :active-page="executions.current_page"
            :pages="executions.last_page"
            align="center"
            @update:activePage="changePage"
        />
    </DCard>
</template>
<script>
import App from '~@/App.vue'
import CronJobLayout from '~@/Pages/Administrations/CronJobs/Layout.vue'
import { __ } from '~@/Composables/translation'
import { ref, onMounted } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
export default {
    layout: [App, CronJobLayout]
}
</script>

<script setup>

const props = defineProps({
    items: {
        type: Array,
        default: () => {
            return []
        }
    },
    executions: {
        type: Object,
        default: () => {
            return {}
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    },
    statusOptions: {
        type: Array,
        default: () => {
            return []
        }
    },
    allJobs: {
        type: Array,
        default: () => {
            return []
        }
    }
})

const data = ref([])
const showNewDataHint = ref(false)

onMounted(() => {
    window.laravelEcho.channel('onecrm.cron_job_executions.updated').listen('.cron_job_executions.status_updated', socketEvent => {
        handleStatusUpdate(socketEvent)
    })
    data.value = props.items
})

const handleStatusUpdate = (socketEvent) => {
    // do something only if event came from same tenant
    if (socketEvent.tenant !== usePage().props.tenant) {
        return
    }

    const cronJobExecution = socketEvent.cronJobExecution
    const existingRowIndex = data.value.findIndex(existingRow => existingRow.id === cronJobExecution.id)

    // A run that is not in the table never replaces a row: a matching one that a refresh would
    // change this page for only raises the hint, anything else is ignored.
    if (existingRowIndex < 0) {
        if (matchesFilters(cronJobExecution) && affectsCurrentPage(cronJobExecution)) {
            showNewDataHint.value = true
        }

        return
    }

    const updatedRow = { ...data.value[existingRowIndex], ...cronJobExecution }
    // A finished run can drop out of the filters, e.g. it is no longer 'running', or it completed
    // after the 'Completed at' date. It still flashes with its new status, then leaves the list
    // the way a refresh would leave it out.
    const stillMatchesFilters = matchesFilters(updatedRow)

    // after 3000ms, remove the highlight-row class from the updated row, or remove the row if it
    // no longer matches the filters, looking it up again in case the rows changed in the meantime
    setTimeout(() => {
        const rowIndex = data.value.findIndex(existingRow => existingRow.id === updatedRow.id)
        if (rowIndex < 0) {
            return
        }

        if (stillMatchesFilters) {
            data.value.splice(rowIndex, 1, { ...data.value[rowIndex], _classes: '' })
        } else {
            data.value.splice(rowIndex, 1)
        }
    }, 3000)

    updatedRow._classes = updatedRow.status === 'failed' ? 'highlight-row' : 'highlight-row-success'
    data.value.splice(existingRowIndex, 1, updatedRow)
}

// Mirrors CronJobExecutionsRepository::getData(), against the filters the list was loaded with.
const matchesFilters = (cronJobExecution) => {
    const filters = props.filters

    if (filters.job && cronJobExecution.job !== filters.job) {
        return false
    }
    if (filters.status && cronJobExecution.status !== filters.status) {
        return false
    }
    if (filters.dateFrom && (!cronJobExecution.started_at || cronJobExecution.started_at.slice(0, 10) < filters.dateFrom)) {
        return false
    }
    if (filters.dateTo && (!cronJobExecution.completed_at || cronJobExecution.completed_at.slice(0, 10) > filters.dateTo)) {
        return false
    }
    if (filters.force !== undefined && filters.force !== null && Boolean(cronJobExecution.force) !== Boolean(filters.force)) {
        return false
    }

    return true
}

// The list is ordered by id desc, so a run that sorts above the last row shown lands on this page
// or an earlier one, and either way shifts the rows of this page by one. A run that sorts below
// it lands on a later page and leaves this one untouched, unless this page still has room.
const affectsCurrentPage = (cronJobExecution) => {
    if (data.value.length < props.executions.per_page) {
        return true
    }

    return cronJobExecution.id > Math.min(...data.value.map(row => row.id))
}

const filterData = ref({
    job: props.filters.job ?? null,
    status: props.filters.status ?? null,
    dateFrom: props.filters.dateFrom ?? null,
    dateTo: props.filters.dateTo ?? null,
    force: props.filters.force ?? null,
    save_filters: null,
    reset_filters: null
})

const resetFilters = () => {
    filterData.value.job = null
    filterData.value.status = null
    filterData.value.dateFrom = null
    filterData.value.dateTo = null
    filterData.value.force = null
    filterData.value.save_filters = 1
    filterData.value.reset_filters = 1
    changePage(props.executions.current_page)
}

const fields = [
    { key: 'id', label: __('administrations.cron_jobs.executions.id') },
    { key: 'job', label: __('administrations.cron_jobs.executions.job') },
    { key: 'status', label: __('administrations.cron_jobs.executions.status') },
    { key: 'started_at', label: __('administrations.cron_jobs.executions.started_at') },
    { key: 'completed_at', label: __('administrations.cron_jobs.executions.completed_at') },
    { key: 'force', label: __('administrations.cron_jobs.executions.force') },
    { key: 'parameters', label: __('administrations.cron_jobs.executions.parameters') },
    { key: 'process_id', label: __('administrations.cron_jobs.executions.process_id') },
    { key: 'actions', label: '', _style: { minWidth: '5%' } }
]
const visitPage = (filters, page) => {
    const query = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
    if (page > 1) {
        query.page = page
    }

    router.get(route('administrations.cron-jobs.executions'), query)
}

const changePage = (page) => {
    filterData.value.save_filters = 1

    visitPage(filterData.value, page)
}

// Reloads with the filters the list was loaded with, not whatever is typed in the inputs.
const refreshPage = () => {
    visitPage(props.filters, props.executions.current_page)
}

const getStatusTagColor = (status) => {
    if (status === 'done' || status === true) {
        return 'success'
    }
    if (status === 'running' || status === false) {
        return 'secondary'
    }

    return 'danger'
}

const downloadFile = (id) => {
    const link = document.createElement('a')
    link.href = route('administrations.cron-jobs.executions.download-file', { job: id })
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

</script>
<style lang="scss" scoped>
.new-data-hint {
    margin-top: 10px;
    cursor: pointer;
}
</style>
